import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, X, ArrowRight } from "lucide-react";

export function PracticeFlow({ cfg }) {
  const navigate = useNavigate();
  const t = useT();
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState({});
  const [saving, setSaving] = useState(false);
  const [startedAt] = useState(() => Date.now());

  const step = cfg.steps[idx];
  const isLast = idx === cfg.steps.length - 1;
  const set = (key, v) => setState((prev) => ({ ...prev, [key]: v }));

  const canAdvance = useMemo(() => {
    if (!step) return false;
    if (step.type === "intro") return true;
    if (step.type === "mood") return typeof state[step.key] === "number";
    if (step.type === "text" || step.type === "textarea") {
      const v = (state[step.key] || "").toString().trim();
      const min = step.type === "textarea" ? (step.minChars ?? 0) : 0;
      return v.length >= min;
    }
    if (step.type === "choice") return !!state[step.key];
    if (step.type === "multi") return Array.isArray(state[step.key]) && state[step.key].length >= (step.min ?? 1);
    if (step.type === "slider") return typeof state[step.key] === "number";
    if (step.type === "custom") return step.validate ? step.validate(state[step.key]) : state[step.key] != null;
    return true;
  }, [step, state]);

  async function finish() {
    setSaving(true);
    try {
      const payload = {};
      let mood_before = null, mood_after = null, note = null;
      for (const s of cfg.steps) {
        if (s.type === "intro") continue;
        const v = state[s.key];
        if (v == null || v === "") continue;
        if (s.type === "mood" && s.key === "mood_before") mood_before = Number(v);
        else if (s.type === "mood" && s.key === "mood_after") mood_after = Number(v);
        else if (s.key === "note") note = String(v);
        else payload[s.key] = v;
      }
      await api.post("/practices/entries", {
        practice_type: cfg.practiceKey,
        payload, mood_before, mood_after, note,
        duration_seconds: Math.floor((Date.now() - startedAt) / 1000),
      });
      try { await api.post("/growth/track", { event: "practice_complete", meta: { practice_type: cfg.practiceKey } }); } catch { /* soft-fail */ }
      toast.success(t("dw.saved"));
      navigate("/app/deep-within");
    } catch (e) {
      toast.error(apiError(e, t("ui_could_not_save")));
    } finally {
      setSaving(false);
    }
  }

  const next = () => (isLast ? finish() : setIdx(idx + 1));
  const back = () => (idx > 0 ? setIdx(idx - 1) : navigate(-1));
  const progress = (idx + 1) / cfg.steps.length;

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid={`flow-${cfg.practiceKey}`}>
      <div className="flex items-center gap-3">
        <button onClick={back} data-testid="flow-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/70 text-foreground transition-colors hover:bg-accent">
          {idx === 0 ? <X className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full transition-all" style={{ width: `${progress * 100}%`, backgroundColor: cfg.color }} />
        </div>
        <span className="min-w-[2.5rem] text-right text-xs text-muted-foreground">{idx + 1}/{cfg.steps.length}</span>
      </div>

      <div className="min-h-[220px]">{renderStep(step, state, set, cfg.color, t)}</div>

      <div className="sticky bottom-0 border-t border-border bg-background/90 pt-4 backdrop-blur">
        <Button
          onClick={next}
          disabled={!canAdvance || saving}
          data-testid="flow-next"
          className="w-full gap-2 rounded-full py-6 text-base"
          style={{ backgroundColor: canAdvance ? cfg.color : undefined }}
        >
          {isLast ? (saving ? t("saving") : t("ui_save_finish")) : t("ui_continue")}
          {!saving ? <ArrowRight className="h-4 w-4" /> : null}
        </Button>
      </div>
    </div>
  );
}

function MoodRow({ value, min, max, color, onPick, testPrefix }) {
  const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className="flex flex-wrap gap-2">
      {nums.map((n) => (
        <button
          key={n}
          type="button"
          data-testid={testPrefix ? `${testPrefix}-${n}` : undefined}
          onClick={() => onPick(n)}
          className="flex h-11 w-11 items-center justify-center rounded-full border text-sm font-display transition-colors"
          style={{ borderColor: color, backgroundColor: value === n ? color : "transparent", color: value === n ? "#fff" : "inherit" }}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function renderStep(step, state, set, color, t) {
  if (!step) return null;
  if (step.type === "intro") {
    const Icon = step.icon;
    return (
      <div className="space-y-4">
        {Icon ? (
          <span className="flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: color + "22" }}>
            <Icon className="h-8 w-8" style={{ color }} />
          </span>
        ) : null}
        <h2 className="font-display text-2xl text-foreground">{step.title}</h2>
        <p className="text-[15px] leading-relaxed text-muted-foreground">{step.body}</p>
      </div>
    );
  }

  const header = (
    <div className="space-y-1">
      <h2 className="font-display text-xl text-foreground">{step.label}</h2>
      {step.hint ? <p className="text-sm text-muted-foreground">{step.hint}</p> : null}
    </div>
  );

  if (step.type === "mood") {
    return <div className="space-y-4">{header}<MoodRow value={state[step.key]} min={1} max={10} color={color} onPick={(n) => set(step.key, n)} testPrefix={`mood-${step.key}`} /></div>;
  }
  if (step.type === "slider") {
    return <div className="space-y-4">{header}<MoodRow value={state[step.key]} min={step.min} max={step.max} color={color} onPick={(n) => set(step.key, n)} testPrefix={`slider-${step.key}`} /></div>;
  }
  if (step.type === "text") {
    return <div className="space-y-4">{header}<Input value={state[step.key] || ""} onChange={(e) => set(step.key, e.target.value)} placeholder={step.placeholder} data-testid={`text-${step.key}`} autoFocus /></div>;
  }
  if (step.type === "textarea") {
    return <div className="space-y-4">{header}<Textarea value={state[step.key] || ""} onChange={(e) => set(step.key, e.target.value)} placeholder={step.placeholder} className="min-h-[160px]" data-testid={`textarea-${step.key}`} autoFocus /></div>;
  }
  if (step.type === "choice") {
    return (
      <div className="space-y-4">{header}
        <div className="space-y-2">
          {step.options.map((o) => {
            const on = state[step.key] === o.value;
            return (
              <button key={o.value} type="button" data-testid={`choice-${step.key}-${o.value}`} onClick={() => set(step.key, o.value)}
                className="flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors"
                style={{ borderColor: on ? color : "var(--border, #E5E1DA)", backgroundColor: on ? color + "22" : "transparent" }}>
                {o.emoji ? <span className="text-xl">{o.emoji}</span> : null}
                <span className="text-sm" style={{ color: on ? color : "inherit", fontWeight: on ? 700 : 400 }}>{o.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (step.type === "multi") {
    const cur = state[step.key] || [];
    const toggle = (v) => set(step.key, cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
    return (
      <div className="space-y-4">{header}
        <div className="flex flex-wrap gap-2">
          {step.options.map((o) => {
            const on = cur.includes(o.value);
            return (
              <button key={o.value} type="button" data-testid={`multi-${step.key}-${o.value}`} onClick={() => toggle(o.value)}
                className="flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm transition-colors"
                style={{ borderColor: on ? color : "var(--border, #E5E1DA)", backgroundColor: on ? color + "22" : "transparent", color: on ? color : "inherit", fontWeight: on ? 700 : 400 }}>
                {o.emoji ? <span>{o.emoji}</span> : null}{o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (step.type === "custom") {
    return <div className="space-y-4">{header}{step.render(state[step.key], (v) => set(step.key, v))}</div>;
  }
  return null;
}
